import { useEffect, useState } from "react";
import { Download, X, Share, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  prompt(): Promise<void>;
}

const DISMISS_KEY = "pwa-install-dismissed-at";
const INSTALL_KEY = "pwa-installed";
const DISMISS_SNOOZE_MS = 1000 * 60 * 60 * 24 * 3; // 3 days

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  // iOS Safari uses navigator.standalone
  // Modern: matchMedia('(display-mode: standalone)')
  const navStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    navStandalone === true
  );
}

function isiOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;
}

function wasDismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_SNOOZE_MS;
  } catch {
    return false;
  }
}

/** Floating install button + iOS manual instructions modal.
 * Hides itself once the PWA is installed, or after the user dismisses
 * (snoozed for 3 days). */
export function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);

  useEffect(() => {
    // NOT: localStorage "yüklendi" notuna göre erken çıkma — kullanıcı uygulamayı
    // SİLDİĞİNDE tarayıcı haber vermez, not bayat kalır ve banner sonsuza dek gizlenirdi.
    // beforeinstallprompt gelmesi "yüklü değil" kanıtıdır; sadece uygulama içindeyken çık.
    if (isStandalone()) {
      return;
    }

    const iOS = isiOS();

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      try { localStorage.removeItem(INSTALL_KEY); } catch { /* empty */ }
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      if (!wasDismissedRecently()) setVisible(true);
    };

    const onInstalled = () => {
      try {
        localStorage.setItem(INSTALL_KEY, "true");
      } catch {
        /* empty */
      }
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari doesn't fire beforeinstallprompt — show our manual hint after
    // a delay, so it doesn't pop the moment the page opens.
    let iosTimer: ReturnType<typeof setTimeout> | undefined;
    if (iOS && !wasDismissedRecently()) {
      iosTimer = setTimeout(() => setVisible(true), 5000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        try {
          localStorage.setItem(INSTALL_KEY, "true");
        } catch {
          /* empty */
        }
      }
      setDeferredPrompt(null);
      setVisible(false);
      return;
    }
    // No native prompt available (iOS Safari, etc.) — show instructions.
    if (isiOS()) setShowIosInstructions(true);
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* empty */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Floating banner above footer — small on desktop, edge-to-edge on mobile */}
      <div
        className={cn(
          "fixed z-[60] bottom-4 left-1/2 -translate-x-1/2 w-[calc(100vw-1rem)] max-w-md",
          "sm:left-auto sm:right-4 sm:translate-x-0",
          "rounded-xl border bg-background/95 backdrop-blur shadow-lg",
          "p-3 flex items-center gap-3",
        )}
        role="dialog"
        aria-label="Uygulamayı Yükle"
      >
        <img src="/logo.png" alt="UGC Video" className="h-10 w-10 rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Uygulamayı Yükle</p>
          <p className="text-xs text-muted-foreground truncate">
            Ana ekrana ekleyin, bildirimleri kaçırmayın
          </p>
        </div>
        <Button size="sm" onClick={handleInstall} className="flex-shrink-0 gap-1">
          <Download className="h-4 w-4" />
          <span className="hidden xs:inline">Yükle</span>
        </Button>
        <Button
          size="icon"
          variant="ghost"
          onClick={handleDismiss}
          aria-label="Kapat"
          className="flex-shrink-0 h-8 w-8"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* iOS manual instructions */}
      {showIosInstructions && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={() => setShowIosInstructions(false)}
        >
          <div
            className="w-full max-w-md rounded-2xl bg-background border shadow-xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <img src="/logo.png" alt="UGC Video" className="h-10 w-10 rounded-lg" />
              <div>
                <p className="font-semibold">Ana Ekrana Ekle</p>
                <p className="text-xs text-muted-foreground">iPhone / iPad için</p>
              </div>
            </div>
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">1</span>
                <span className="flex-1">
                  Alt (veya üst) menüden
                  <Share className="inline-block h-4 w-4 mx-1 align-text-bottom" />
                  <strong>Paylaş</strong> simgesine dokunun.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">2</span>
                <span className="flex-1">
                  Açılan listeden
                  <Plus className="inline-block h-4 w-4 mx-1 align-text-bottom" />
                  <strong>Ana Ekrana Ekle</strong> seçeneğine dokunun.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">3</span>
                <span className="flex-1">
                  Sağ üstteki <strong>Ekle</strong>'ye dokunarak tamamlayın.
                </span>
              </li>
            </ol>
            <div className="mt-5 flex justify-end">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowIosInstructions(false);
                  handleDismiss();
                }}
              >
                Kapat
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PwaInstallPrompt;
